import * as UserAction from '@ori-events/user-action';
import { alpha, Link, styled, Typography } from '@ori-ui/mui';
import type { Maybe, Navigation } from '@ori/graphql-schema';
import { Banner } from '@ori/presentation-components';
import { shouldForwardProp } from '@ori/presentation-utils';
import useTestId from '@ori/testid-generator';
import type { VFC } from 'react';

import { ProductImage } from './CatalogueImage';
import { SkeletonCatalogueDesktop, SkeletonCatalogueMobile } from '~/components/Skeleton';
import {
  CLICK_CATALOGUE_DETAIL,
  DESKTOP_BREAKPOINT,
  TENANTS_LIST_WHERE_CATALOG_HEADER_IS_HIDDEN,
} from '~/constants';
import { useAppConfigurationContext } from '~/contexts/AppConfigurationContext';
import { useCataloguesContext } from '~/contexts/CataloguesContext';
import { useNavigationContext } from '~/contexts/NavigationContext';
import { useMobileWidth, useTranslationByKey } from '~/hooks';
import { useIsMarketCN } from '~/hooks/useIsMarketCN';
import { TranslationKeys } from '~/models';
import { formatDate } from '~/utils';

interface IsMarketCNProps {
  isMarketCN: boolean;
}

interface IsMobileProps {
  isMobile: boolean;
}

const CataloguesRoot = styled('div', {
  shouldForwardProp: shouldForwardProp(),
})<IsMarketCNProps & IsMobileProps>(({ isMarketCN, isMobile, theme }) => ({
  alignItems: 'center',
  background: theme.palette.common.white,
  color: theme.palette.text.primary,
  display: isMobile ? 'unset' : 'flex',
  flexDirection: 'column',

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    alignItems: 'center',
    columnGap: 20,
    display: 'grid',
    flexDirection: 'row',
    gridTemplateColumns: '310px auto',
    gridTemplateRows: isMarketCN ? '83px 286px 37px' : '83px 440px 37px',
    justifyContent: 'center',
    marginBottom: theme.spacing(6),
    width: '100%',
  },
}));

const CataloguesCurrentDetails = styled('div')(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    alignSelf: 'flex-end',
    gridColumn: 1,
    gridRow: 1,
  },
}));

const CataloguesCurrentTitle = styled(Link)(({ theme }) => ({
  display: 'block',
  fontSize: theme.typography.pxToRem(17),
  fontWeight: theme.typography.fontWeightBold,
  lineHeight: theme.typography.pxToRem(25),
  textAlign: 'center',
  textDecoration: 'none',

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    '&:hover': {
      color: theme.palette.grey.A400,
    },
  },
}));

const CataloguesCurrentValidity = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey.A700,
  fontSize: theme.typography.body1.fontSize,
  lineHeight: theme.typography.pxToRem(17),
  margin: theme.spacing(2, 0, 3),
  textAlign: 'center',
}));

const CataloguesCurrentImage = styled(Link, {
  shouldForwardProp: shouldForwardProp(),
})<IsMarketCNProps>(({ isMarketCN, theme }) => ({
  display: 'block',
  height: isMarketCN ? 231 : 355,
  WebkitTapHighlightColor: 'transparent',
  width: 250,

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    alignSelf: 'flex-start',
    gridColumn: 1,
    gridRow: 2,
    height: isMarketCN ? 286 : 440,
    width: 310,

    '&:hover::before': {
      background: alpha(theme.palette.common.black, 0.3),
      // TODO(10/05/2022): Remove after new release of ori-ui.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any -- not working theme augmentation
      borderRadius: (theme.shape as any).largeComponentRadius,
      content: '""',
      height: isMarketCN ? 286 : 440,
      position: 'absolute',
      width: 310,
    },
  },
}));

const CataloguesOtherLabel = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    alignSelf: 'flex-end',
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    gridColumn: 2,
    gridRow: 1,
    lineHeight: theme.typography.pxToRem(21),
    marginBottom: theme.spacing(4),
    textAlign: 'center',
  },
}));

const CataloguesNextImage = styled(Link, {
  shouldForwardProp: shouldForwardProp(),
})<IsMarketCNProps>(({ isMarketCN, theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    alignSelf: 'flex-start',
    display: 'block',
    gridColumn: 2,
    gridRow: 2,
    height: isMarketCN ? 121 : 186,
    WebkitTapHighlightColor: 'transparent',
    width: 131,

    '&:hover::before': {
      background: alpha(theme.palette.common.black, 0.3),
      content: '""',
      height: isMarketCN ? 121 : 186,
      position: 'absolute',
      width: 131,
    },
  },
}));

const CataloguesSeeAll = styled('div')<{ ownerState: { isSingleCatalogue: boolean } }>(
  ({ ownerState, theme }) => ({
    marginTop: theme.spacing(3),
    textAlign: 'center',

    [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
      alignSelf: 'flex-start',
      gridColumn: 2,
      gridRow: 3,
      marginTop: theme.spacing(4),

      ...(ownerState.isSingleCatalogue && {
        gridColumn: 1,
        gridRow: 3,
      }),
    },
  }),
);

const CataloguesSeeAllLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: theme.typography.body1.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  lineHeight: theme.typography.pxToRem(17),
  textTransform: 'uppercase',
  WebkitTapHighlightColor: 'transparent',
  textDecorationThickness: '2px',
  textUnderlineOffset: '5px',
}));

const BannerWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(4, 4, 1, 5),
}));

export const Catalogues: VFC = () => {
  const { catalogues, isDataFetched } = useCataloguesContext();
  const { shopSection } = useNavigationContext();
  const isMarketCN = useIsMarketCN();
  const isMobile = useMobileWidth();
  const translations = useTranslationByKey();
  const { getTestId } = useTestId();
  const { tenant } = useAppConfigurationContext();

  if (!isDataFetched) {
    return isMobile ? <SkeletonCatalogueMobile /> : <SkeletonCatalogueDesktop />;
  }

  const children = shopSection as Maybe<Array<Navigation | null>> | undefined;

  if (!children) {
    return null;
  }

  const formatValidity = (catalogueId: string) => {
    const catalogue = catalogues?.find((e) => e.catalogueId.toString() === catalogueId);

    if (!catalogue?.valid) {
      return null;
    }

    const from = formatDate(catalogue.valid.from);
    const to = formatDate(catalogue.valid.to);

    if (!from || !to) {
      return null;
    }

    return `${from} - ${to}`;
  };
  const link = children.find((e) => e?.linkType === 'Catalogue');
  const catalogueCurrent = link?.children?.find((e) => e?.linkType === 'DigitalCatalogueCurrent');
  const catalogueNext = link?.children?.find((e) => e?.linkType === 'DigitalCatalogueNext');
  const cataloguesAll = link?.children?.find((e) => e?.linkType === 'Catalogues');
  const isCatalogueNextValid = Boolean(
    !isMobile && catalogueNext?.imageUrl && catalogueNext.title && catalogueNext.url,
  );
  const ownerState = { isSingleCatalogue: Boolean(cataloguesAll) };

  if (
    !catalogueCurrent?.id ||
    !catalogueCurrent.imageUrl ||
    !catalogueCurrent.title ||
    !catalogueCurrent.url
  ) {
    return null;
  }

  return (
    <CataloguesRoot
      isMarketCN={isMarketCN}
      isMobile={isMobile}
      data-testid={getTestId('catalogues-root')}
    >
      {isMobile ? (
        <BannerWrapper>
          <Banner
            type="catalogue"
            dateRange={formatValidity(catalogueCurrent.id) ?? ''}
            title={catalogueCurrent.title}
            imageUrl={catalogueCurrent.imageUrl}
            url={catalogueCurrent.url}
          />
        </BannerWrapper>
      ) : (
        <>
          <CataloguesCurrentDetails data-testid={getTestId('catalogues-current-details')}>
            {TENANTS_LIST_WHERE_CATALOG_HEADER_IS_HIDDEN.includes(tenant) ? null : (
              <>
                <CataloguesCurrentTitle
                  data-testid={getTestId('catalogues-current-title')}
                  href={catalogueCurrent.url}
                >
                  {catalogueCurrent.title}
                </CataloguesCurrentTitle>
                <CataloguesCurrentValidity data-testid={getTestId('catalogues-current-validity')}>
                  {formatValidity(catalogueCurrent.id)}
                </CataloguesCurrentValidity>
              </>
            )}
          </CataloguesCurrentDetails>
          <CataloguesCurrentImage
            isMarketCN={isMarketCN}
            href={catalogueCurrent.url}
            data-testid={getTestId('catalogues-current-image')}
            onClick={() => UserAction.dispatch(CLICK_CATALOGUE_DETAIL)}
          >
            <ProductImage
              alt={catalogueCurrent.title}
              image={catalogueCurrent.imageUrl}
              catalogue="current"
              data-testid={getTestId('product-image')}
            />
          </CataloguesCurrentImage>
        </>
      )}
      {isCatalogueNextValid ? (
        <>
          <CataloguesOtherLabel data-testid={getTestId('catalogues-other-label')}>
            {translations(TranslationKeys.Other)}
          </CataloguesOtherLabel>
          <CataloguesNextImage
            isMarketCN={isMarketCN}
            href={catalogueNext?.url as string}
            data-testid={getTestId('catalogues-next-image')}
          >
            <ProductImage
              alt={catalogueNext?.title as string}
              image={catalogueNext?.imageUrl as string}
              catalogue="next"
              data-testid={getTestId('product-image')}
            />
          </CataloguesNextImage>
        </>
      ) : null}
      {cataloguesAll?.url ? (
        <CataloguesSeeAll
          ownerState={ownerState}
          data-testid={getTestId('catalogues-see-all')}
        >
          <CataloguesSeeAllLink
            href={cataloguesAll.url}
            data-testid={getTestId('catalogues-see-all-link')}
          >
            {translations(TranslationKeys.SeeAll)}
          </CataloguesSeeAllLink>
        </CataloguesSeeAll>
      ) : null}
    </CataloguesRoot>
  );
};
